import { makeStyles } from 'tss-react/mui';
import React from 'react';
import Colors from '../../../utils/color';
import TruncationTooltip from './TruncationTooltip';

const useStyles = makeStyles<{ isSelected: boolean; is86ed: boolean }>()(
  (_theme, { isSelected, is86ed }) => ({
    // 24px borderless row, against the 40px chip locked to `width: 18%` it
    // replaces. The box was worth about 1px of row height, so this is about ink,
    // not space.
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      minWidth: 0,
      height: 24,
      padding: '0 4px',
      cursor: is86ed ? 'not-allowed' : 'pointer',
      color: is86ed ? Colors.Thunderbird : Colors.daintree,
      textDecoration: is86ed ? 'line-through' : 'none',
      fontWeight: isSelected ? 700 : 400,
    },
    tick: {
      flexShrink: 0,
      width: 12,
      fontSize: '11px',
      color: isSelected ? Colors.readBack : 'transparent',
    },
    name: {
      flex: 1,
      minWidth: 0,
      fontSize: '13px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    // Marks an option that brings further groups — a doorway, not a leaf.
    furtherGroups: {
      flexShrink: 0,
      fontSize: '12px',
      color: Colors.silverChalice,
    },
  })
);

interface ModOptionRowProps {
  name: string;
  isSelected: boolean;
  is86ed: boolean;
  bringsFurtherGroups: boolean;
  onSelect: () => void;
}

/**
 * HITL-1616 — one option inside the drawer.
 *
 * Selection is a `✓` plus weight and colour rather than a bordered box, and an
 * unavailable option is red + line-through with **no "86" badge** — the
 * `SingularModChoice` precedent, kept.
 *
 * No price. The frozen design carries none, and the console could not show a
 * correct one anyway: both chip callers pass no `modalityState`, so
 * `getMenuItemPrice` falls back to `ModalityType.Dinein` on a drive-thru. Prices
 * stay where they are true — the cart line.
 */
const ModOptionRow = ({
  name,
  isSelected,
  is86ed,
  bringsFurtherGroups,
  onSelect,
}: ModOptionRowProps) => {
  const { classes } = useStyles({ isSelected, is86ed });

  const onClick = () => {
    if (is86ed) return;
    onSelect();
  };

  return (
    <div
      className={classes.row}
      onClick={onClick}
      data-testid="mod-option-row"
      data-selected={isSelected ? 'true' : 'false'}
      role="button"
      tabIndex={is86ed ? -1 : 0}
      aria-disabled={is86ed}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onClick();
      }}
    >
      <span className={classes.tick}>✓</span>
      <TruncationTooltip title={name}>
        <span className={classes.name}>{name}</span>
      </TruncationTooltip>
      {bringsFurtherGroups && (
        <span className={classes.furtherGroups} data-testid="brings-groups">
          ›
        </span>
      )}
    </div>
  );
};

export default ModOptionRow;
